import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, LayoutGrid, MessageSquareOff, FileSpreadsheet, Layers,
  Crown, Building2, Users, GraduationCap, Home as HomeIcon, TrendingUp, ShieldCheck,
  FileText, ExternalLink, Mail,
} from "lucide-react";
import { LaunchingSoon } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import { FourPillars } from "../components/site/HomeParts";
import { InvestorDialog } from "../components/site/InvestorDialog";
import { TRACTION } from "../lib/site";

const fragmentedTools = [
  { icon: LayoutGrid, t: "ERPs" },
  { icon: MessageSquareOff, t: "Messaging apps" },
  { icon: FileSpreadsheet, t: "Spreadsheets" },
  { icon: Layers, t: "LMS & records" },
];

const roles = [
  { icon: Crown, l: "Principal" },
  { icon: Building2, l: "Administration" },
  { icon: Users, l: "Teacher" },
  { icon: GraduationCap, l: "Student" },
  { icon: HomeIcon, l: "Parent" },
];

const highlights = [
  { icon: TrendingUp, t: "Category creation", d: "A new software category — the School Operating System — with a connection-first moat." },
  { icon: Layers, t: "Ecosystem lock-in", d: "Six connected experiences per school create deep, multi-stakeholder engagement." },
  { icon: FileText, t: "Product & prototype ready", d: "Multi-platform UI/UX and interactive prototypes; architecture developed." },
  { icon: ShieldCheck, t: "Recurring model", d: "Native subscription and billing management drives durable per-school ARR." },
];

/* Hero */
const BriefHero = () => (
  <section className="chrome pt-32 pb-16 md:pt-40 md:pb-20 relative overflow-hidden" data-testid="brief-hero">
    <div className="absolute inset-0 grid-lines opacity-50" />
    <div className="absolute inset-0 radial-glow" />
    <div className="orb orb-indigo animate-drift -top-32 -right-24 h-[480px] w-[480px]" />
    <div className="orb orb-green animate-drift-slow -bottom-24 -left-32 h-[420px] w-[420px]" />
    <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
      <div className="flex justify-center mb-6">
        <LaunchingSoon tone="dark" label="Launching Soon · Investor Brief · v1" />
      </div>
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
        className="font-display text-4xl md:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
        PARTECON — <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A5B4FC] to-[#818CF8]">The School Operating System</span>
      </motion.h1>
      <p className="text-white/60 text-lg md:text-xl leading-relaxed mt-6 max-w-3xl mx-auto">
        A one-page investor brief. PARTECON connects classroom execution, school operations,
        communication and intelligence into one live operating layer — the connective tissue for how modern schools run.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11.5px] font-mono-accent uppercase tracking-widest text-white/45">
        <span data-testid="brief-meta-category">Category · School OS</span>
        <span className="hidden sm:inline">·</span>
        <span data-testid="brief-meta-stage">Stage · Pre-launch / Pilot</span>
        <span className="hidden sm:inline">·</span>
        <span data-testid="brief-meta-founded">Founded · 2024</span>
      </div>
    </div>
  </section>
);

/* Problem */
const BriefProblem = () => (
  <section className="canvas py-16 md:py-20" data-testid="brief-problem">
    <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="overline">01 · The problem</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-[#1A1D26]">
          Schools run on 5+ disconnected systems.
        </h2>
        <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5 max-w-3xl">
          Information scatters across ERPs, messaging apps, spreadsheets and manual reports.
          Fragmented visibility · duplicated work · decisions on stale data.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-7">
          {fragmentedTools.map((f) => (
            <div key={f.t} className="rounded-lg bg-white border border-[#ECECE8] px-3 py-2.5 flex items-center gap-2">
              <f.icon className="text-[#9AA1AD] shrink-0" size={15} />
              <span className="text-[12px] text-[#6B7280]">{f.t}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* Thesis + Pillars */
const BriefThesis = () => (
  <section className="chrome py-16 md:py-20 relative overflow-hidden" data-testid="brief-thesis">
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl">
        <span className="overline overline-light">02 · The thesis</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-white">
          One operating layer for the whole school.
        </h2>
        <p className="text-white/60 text-base md:text-lg leading-relaxed mt-5">
          PARTECON consolidates the school's daily functions into <span className="text-white">four operating pillars</span>,
          held together by three architectural primitives:
          <span className="text-white"> EOC</span> (End-of-Class Summary),
          <span className="text-white"> PCC</span> (Communication Channel),
          <span className="text-white"> AHI</span> (Analytical Health Index).
        </p>
      </Reveal>
      <div className="mt-10"><FourPillars /></div>
    </div>
  </section>
);

/* Product / roles */
const BriefProduct = () => (
  <section className="canvas py-16 md:py-20" data-testid="brief-product">
    <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl">
        <span className="overline">03 · The product</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-[#1A1D26]">
          Five role experiences. One connected foundation.
        </h2>
        <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">
          Every stakeholder gets a purpose-built experience — but every experience runs on the same
          PARTECON operating layer. It's not five products. It's one system, seen from five angles.
        </p>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-9">
        {roles.map((r) => (
          <div key={r.l} className="rounded-2xl bg-white border border-[#ECECE8] p-5 text-center">
            <span className="mx-auto h-11 w-11 rounded-xl bg-[#EEF0FB] flex items-center justify-center">
              <r.icon size={19} className="text-[#6366F1]" />
            </span>
            <p className="text-[#1A1D26] font-medium text-[14px] mt-3.5">{r.l}</p>
          </div>
        ))}
      </div>
      <Link to="/platform" className="inline-flex items-center gap-2 mt-8 text-[14px] font-semibold text-[#4F46E5] hover:gap-3 transition-all" data-testid="brief-product-link">
        See the full product architecture <ArrowRight size={15} />
      </Link>
    </div>
  </section>
);

/* Traction */
const BriefTraction = () => (
  <section className="chrome py-16 md:py-20 relative overflow-hidden" data-testid="brief-traction">
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl">
        <span className="overline overline-light">04 · Traction</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-white">
          Field-validated. Founder-funded.
        </h2>
      </Reveal>
      <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden mt-8">
        {TRACTION.map((t) => (
          <StaggerItem key={t.label}>
            <div className="bg-[#0E1117] p-7 text-center h-full">
              <div className="font-display text-4xl md:text-5xl font-semibold text-[#A5B4FC]">{t.value}</div>
              <p className="text-white/55 text-sm mt-3">{t.label}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

/* Why now / highlights */
const BriefWhyNow = () => (
  <section className="canvas py-16 md:py-20" data-testid="brief-why-now">
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-3xl">
        <span className="overline">05 · Why now</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-[#1A1D26]">
          A new software category, at the right moment.
        </h2>
      </Reveal>
      <Stagger className="grid md:grid-cols-2 gap-4 mt-8">
        {highlights.map((h) => (
          <StaggerItem key={h.t}>
            <div className="rounded-2xl bg-white border border-[#ECECE8] p-6 md:p-7 h-full flex gap-4 card-hover">
              <span className="h-11 w-11 shrink-0 rounded-xl bg-[#EEF0FB] flex items-center justify-center">
                <h.icon size={19} className="text-[#6366F1]" />
              </span>
              <div>
                <h3 className="font-display text-[17px] font-semibold text-[#1A1D26]">{h.t}</h3>
                <p className="text-[#6B7280] text-[14px] mt-2 leading-relaxed">{h.d}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

/* What's next */
const BriefNext = () => (
  <section className="chrome py-16 md:py-20 relative overflow-hidden" data-testid="brief-next">
    <div className="absolute inset-0 radial-glow" />
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <span className="overline overline-light">06 · What's next</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-white">
          Pilot cohort forming for the 2026 school year.
        </h2>
        <p className="text-white/60 text-base md:text-lg leading-relaxed mt-5 max-w-2xl mx-auto">
          We're onboarding a focused group of design-partner schools now. Investor conversations are open —
          the pitch deck, business plan and interactive product are available on request.
        </p>
        <div className="flex flex-col sm:flex-row gap-3.5 justify-center mt-9">
          <InvestorDialog title="Access the Pitch Deck" description="Share your name and email to open the PARTECON pitch deck." submitLabel="View Pitch Deck" deckOnSuccess>
            <button data-testid="brief-deck-btn" className="btn-primary px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2">View Pitch Deck <ExternalLink size={14} /></button>
          </InvestorDialog>
          <Link to="/contact" data-testid="brief-demo-btn" className="btn-ghost px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2">Talk to us <ArrowUpRight size={14} /></Link>
          <a href="mailto:aleem@partecon.com" data-testid="brief-email-btn" className="btn-ghost px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2">Email us <Mail size={14} /></a>
        </div>
        <p className="text-white/40 text-xs mt-8 font-mono-accent tracking-widest uppercase">
          aleem@partecon.com · PARTECON Private Limited
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Brief() {
  return (
    <div data-testid="brief-page">
      <BriefHero />
      <BriefProblem />
      <BriefThesis />
      <BriefProduct />
      <BriefTraction />
      <BriefWhyNow />
      <BriefNext />
    </div>
  );
}
